<template>
  <div class="main-page">
    <section class="main-hero">
      <div>
        <div class="main-hero__label">
          Главная
        </div>
        <h1>Здравствуйте, {{ user.fullName }}</h1>
        <p>
          Соберите обучение сотрудников в одном месте: материалы, тесты, курсы и база знаний
          теперь под рукой
        </p>
      </div>

      <div class="main-hero__actions">
        <router-link
          v-for="action in quickActions"
          :key="action.title"
          :to="action.route"
          class="quick-action"
        >
          <span class="quick-action__icon">
            <feather-icon
              :icon="action.icon"
              size="18"
            />
          </span>
          <span>
            <span class="quick-action__title">{{ action.title }}</span>
            <span class="quick-action__text">{{ action.text }}</span>
          </span>
        </router-link>
      </div>
    </section>

    <section class="main-stats">
      <router-link
        v-for="stat in stats"
        :key="stat.title"
        :to="stat.route"
        class="stat-card"
      >
        <span class="stat-card__icon">
          <feather-icon
            :icon="stat.icon"
            size="19"
          />
        </span>
        <span class="stat-card__value">{{ stat.value }}</span>
        <span class="stat-card__title">{{ stat.title }}</span>
      </router-link>
    </section>

    <div class="main-grid">
      <section
        v-if="!isGettingStartedHidden"
        class="getting-started"
      >
        <div class="section-head">
          <div>
            <div class="section-head__title">
              С чего начать работу с Eddy24
            </div>
            <div class="section-head__text">
              Быстрый маршрут, который помогает запустить обучение без лишней суеты.
            </div>
          </div>

          <button
            type="button"
            class="icon-button"
            aria-label="Скрыть блок с подсказками"
            @click="hideGettingStarted"
          >
            <feather-icon
              icon="XIcon"
              size="18"
            />
          </button>
        </div>

        <div class="start-steps">
          <router-link
            v-for="(step, index) in gettingStartedSteps"
            :key="step.title"
            :to="step.route"
            class="start-step"
          >
            <span class="start-step__number">{{ index + 1 }}</span>
            <span class="start-step__content">
              <span class="start-step__title">{{ step.title }}</span>
              <span class="start-step__text">{{ step.text }}</span>
            </span>
            <span class="start-step__action">
              {{ step.action }}
              <feather-icon
                icon="ArrowRightIcon"
                size="16"
              />
            </span>
          </router-link>
        </div>
      </section>

      <section
        v-else
        class="workspace-panel"
      >
        <div class="section-head">
          <div>
            <div class="section-head__title">
              Рабочая панель
            </div>
            <div class="section-head__text">
              Первые шаги скрыты. Здесь остаются быстрые переходы для ежедневной работы.
            </div>
          </div>

          <button
            type="button"
            class="restore-button"
            @click="restoreGettingStarted"
          >
            Вернуть подсказки
          </button>
        </div>

        <div class="workspace-actions">
          <router-link
            v-for="action in workspaceActions"
            :key="action.title"
            :to="action.route"
            class="workspace-action"
          >
            <span class="workspace-action__icon">
              <feather-icon
                :icon="action.icon"
                size="18"
              />
            </span>
            <span>
              <span class="workspace-action__title">{{ action.title }}</span>
              <span class="workspace-action__text">{{ action.text }}</span>
            </span>
            <feather-icon
              icon="ArrowRightIcon"
              size="16"
              class="workspace-action__arrow"
            />
          </router-link>
        </div>
      </section>

      <LastUpdate />
    </div>

    <section class="main-insights">
      <div class="focus-card">
        <div class="focus-card__icon">
          <feather-icon
            icon="TargetIcon"
            size="22"
          />
        </div>
        <div>
          <div class="focus-card__title">
            Фокус на сегодня
          </div>
          <div class="focus-card__text">
            {{ focusText }}
          </div>
        </div>
        <router-link
          :to="focusRoute"
          class="focus-card__button"
        >
          Перейти
          <feather-icon
            icon="ArrowRightIcon"
            size="16"
          />
        </router-link>
      </div>

      <div class="help-card">
        <div class="help-card__title">
          Нужна подсказка?
        </div>
        <div class="help-card__text">
          В FAQ собраны ответы по настройке курсов, тестов, материалов и личного кабинета сотрудника.
        </div>
        <router-link
          to="/faq"
          class="help-card__link"
        >
          Открыть FAQ
        </router-link>
      </div>
    </section>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import LastUpdate from '@/components/base/LastUpdate.vue'

const GETTING_STARTED_STORAGE_KEY = 'eddy24-main-getting-started-hidden'

export default {
  name: 'MainPage',
  components: {
    LastUpdate,
  },
  data() {
    return {
      isGettingStartedHidden: false,
      quickActions: [
        {
          title: 'Создать курс',
          text: 'Соберите программу обучения',
          route: '/course/0',
          icon: 'ColumnsIcon',
        },
        {
          title: 'Добавить тест',
          text: 'Проверьте знания сотрудников',
          route: '/test/0',
          icon: 'CheckSquareIcon',
        },
        {
          title: 'Добавить материал',
          text: 'Сохраните инструкции и регламенты',
          route: '/material/0',
          icon: 'BookOpenIcon',
        },
      ],
      gettingStartedSteps: [
        {
          title: 'Добавьте сотрудников',
          text: 'Создайте команду и проверьте контактные данные для отправки доступов.',
          action: 'Открыть сотрудников',
          route: '/employees',
        },
        {
          title: 'Подготовьте материалы',
          text: 'Загрузите инструкции, статьи и файлы, которые будут использоваться в курсах.',
          action: 'Создать материал',
          route: '/material/0',
        },
        {
          title: 'Соберите тест',
          text: 'Добавьте вопросы, варианты ответов и настройте проверку знаний.',
          action: 'Создать тест',
          route: '/test/0',
        },
        {
          title: 'Опубликуйте курс',
          text: 'Объедините материалы и тесты в понятную программу обучения.',
          action: 'Создать курс',
          route: '/course/0',
        },
      ],
      workspaceActions: [
        {
          title: 'Проверить статистику',
          text: 'Посмотреть прогресс сотрудников',
          route: '/statistics',
          icon: 'PieChartIcon',
        },
        {
          title: 'Открыть сотрудников',
          text: 'Назначить обучение или отправить доступ',
          route: '/employees',
          icon: 'UsersIcon',
        },
        {
          title: 'Перейти к курсам',
          text: 'Обновить программу обучения',
          route: '/courses',
          icon: 'ColumnsIcon',
        },
        {
          title: 'База знаний',
          text: 'Навести порядок в разделах',
          route: '/wiki',
          icon: 'BookIcon',
        },
      ],
    }
  },
  computed: {
    ...mapState('main', ['articles', 'categories', 'courses', 'tests', 'user']),
    stats() {
      return [
        {
          title: 'Курсы',
          value: this.countItems(this.courses),
          route: '/courses',
          icon: 'ColumnsIcon',
        },
        {
          title: 'Тесты',
          value: this.countItems(this.tests),
          route: '/tests',
          icon: 'CheckSquareIcon',
        },
        {
          title: 'Материалы',
          value: this.countItems(this.articles),
          route: '/materials',
          icon: 'BookOpenIcon',
        },
        {
          title: 'Разделы базы знаний',
          value: this.countItems(this.categories),
          route: '/wiki',
          icon: 'BookIcon',
        },
      ]
    },
    focusText() {
      if (!this.countItems(this.articles)) {
        return 'Начните с первого материала: он станет основой для курсов и базы знаний.'
      }

      if (!this.countItems(this.tests)) {
        return 'Добавьте тест, чтобы закрепить материалы и видеть реальные результаты обучения.'
      }

      if (!this.countItems(this.courses)) {
        return 'Соберите первый курс из уже созданных материалов и тестов.'
      }

      return 'Проверьте статистику: там видно, кто уже проходит обучение и где нужна помощь.'
    },
    focusRoute() {
      if (!this.countItems(this.articles)) return '/material/0'
      if (!this.countItems(this.tests)) return '/test/0'
      if (!this.countItems(this.courses)) return '/course/0'
      return '/statistics'
    },
  },
  mounted() {
    this.isGettingStartedHidden = localStorage.getItem(GETTING_STARTED_STORAGE_KEY) === 'true'
  },
  methods: {
    countItems(value) {
      return Array.isArray(value) ? value.length : 0
    },
    hideGettingStarted() {
      this.isGettingStartedHidden = true
      localStorage.setItem(GETTING_STARTED_STORAGE_KEY, 'true')
    },
    restoreGettingStarted() {
      this.isGettingStartedHidden = false
      localStorage.removeItem(GETTING_STARTED_STORAGE_KEY)
    },
  },
}
</script>

<style lang="scss">
.main-page {
  width: 100%;
  color: #111827;

  a {
    color: inherit;

    &:hover {
      color: inherit;
      text-decoration: none;
    }
  }

  .main-hero {
    width: 100%;
    min-height: 220px;
    display: flex;
    align-items: stretch;
    justify-content: space-between;
    gap: 28px;
    padding: 30px;
    margin-bottom: 20px;
    border-radius: 8px;
    background: #0a74b8;
    box-shadow: 0 20px 45px rgba(0, 105, 160, 0.18);
    color: #ffffff;

    &__label {
      font-size: 13px;
      line-height: 18px;
      font-weight: 600;
      text-transform: uppercase;
      margin-bottom: 14px;
      color: rgba(255, 255, 255, 0.72);
    }

    h1 {
      max-width: 640px;
      margin: 0 0 14px;
      font-size: 32px;
      line-height: 40px;
      font-weight: 600;
      letter-spacing: 0;
      color: #ffffff;
    }

    p {
      max-width: 620px;
      margin: 0;
      font-size: 16px;
      line-height: 24px;
      color: rgba(255, 255, 255, 0.78);
    }

    &__actions {
      width: 360px;
      display: grid;
      gap: 10px;
      align-content: center;
      flex-shrink: 0;
    }
  }

  .quick-action {
    display: flex;
    align-items: center;
    gap: 14px;
    min-height: 64px;
    padding: 12px 14px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.12);
    color: #ffffff;
    transition: transform .2s, background .2s, border-color .2s;

    &:hover {
      transform: translateY(-2px);
      background: rgba(255, 255, 255, 0.18);
      border-color: rgba(255, 255, 255, 0.32);
    }

    &__icon {
      width: 38px;
      height: 38px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 8px;
      background: rgba(255, 255, 255, 0.16);
      flex-shrink: 0;
    }

    &__title,
    &__text {
      display: block;
    }

    &__title {
      font-size: 15px;
      line-height: 20px;
      font-weight: 600;
    }

    &__text {
      margin-top: 2px;
      font-size: 13px;
      line-height: 18px;
      color: rgba(255, 255, 255, 0.72);
    }
  }

  .main-stats {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
    margin-bottom: 20px;
  }

  .stat-card {
    min-height: 126px;
    padding: 18px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    border-radius: 8px;
    background: #ffffff;
    border: 1px solid #eef1f4;
    box-shadow: 0 12px 26px rgba(17, 24, 39, 0.05);
    transition: transform .2s, box-shadow .2s, border-color .2s;

    &:hover {
      transform: translateY(-2px);
      border-color: rgba(0, 136, 204, 0.25);
      box-shadow: 0 18px 32px rgba(17, 24, 39, 0.08);
    }

    &__icon {
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 8px;
      color: #0088cc;
      background: rgba(0, 136, 204, 0.1);
    }

    &__value {
      margin-top: 18px;
      font-size: 30px;
      line-height: 34px;
      font-weight: 600;
      color: #111827;
    }

    &__title {
      margin-top: 4px;
      font-size: 14px;
      line-height: 20px;
      color: #6b7280;
    }
  }

  .main-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
    align-items: stretch;
  }

  .getting-started,
  .workspace-panel,
  .focus-card,
  .help-card {
    background: #ffffff;
    border: 1px solid #eef1f4;
    border-radius: 8px;
    box-shadow: 0 12px 26px rgba(17, 24, 39, 0.05);
  }

  .getting-started,
  .workspace-panel {
    width: 100%;
    height: 100%;
    padding: 24px;
  }

  .section-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
    margin-bottom: 22px;

    &__title {
      font-size: 20px;
      line-height: 28px;
      font-weight: 600;
      color: #111827;
    }

    &__text {
      margin-top: 6px;
      max-width: 560px;
      font-size: 14px;
      line-height: 20px;
      color: #6b7280;
    }
  }

  .icon-button {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 8px;
    color: #6b7280;
    background: #f3f4f6;
    transition: color .2s, background .2s;
    cursor: pointer;

    &:hover {
      color: #111827;
      background: #e5e7eb;
    }
  }

  .restore-button {
    min-height: 36px;
    padding: 0 12px;
    border: none;
    border-radius: 8px;
    color: #0088cc;
    background: rgba(0, 136, 204, 0.1);
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    transition: background .2s, transform .2s;

    &:hover {
      transform: translateY(-1px);
      background: rgba(0, 136, 204, 0.16);
    }
  }

  .start-steps {
    display: grid;
    gap: 12px;
  }

  .start-step {
    display: grid;
    grid-template-columns: 40px minmax(0, 1fr) auto;
    align-items: center;
    gap: 16px;
    padding: 16px;
    border-radius: 8px;
    background: #f9fafb;
    border: 1px solid #eef1f4;
    transition: transform .2s, background .2s, border-color .2s;

    &:hover {
      transform: translateX(3px);
      background: #ffffff;
      border-color: rgba(0, 136, 204, 0.25);
    }

    &__number {
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 8px;
      color: #0088cc;
      background: rgba(0, 136, 204, 0.1);
      font-weight: 600;
      font-size: 16px;
      line-height: 20px;
    }

    &__title,
    &__text {
      display: block;
    }

    &__title {
      font-size: 16px;
      line-height: 22px;
      font-weight: 600;
      color: #111827;
    }

    &__text {
      margin-top: 4px;
      font-size: 14px;
      line-height: 20px;
      color: #6b7280;
    }

    &__action {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 14px;
      line-height: 20px;
      font-weight: 600;
      color: #0088cc;
      white-space: nowrap;
    }
  }

  .workspace-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .workspace-action {
    min-height: 132px;
    display: grid;
    grid-template-columns: 42px minmax(0, 1fr);
    align-content: start;
    gap: 14px;
    padding: 18px;
    border-radius: 8px;
    border: 1px solid #eef1f4;
    background: #f9fafb;
    position: relative;
    transition: transform .2s, background .2s, border-color .2s;

    &:hover {
      transform: translateY(-2px);
      background: #ffffff;
      border-color: rgba(0, 136, 204, 0.25);
    }

    &__icon {
      width: 42px;
      height: 42px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 8px;
      color: #0088cc;
      background: rgba(0, 136, 204, 0.1);
    }

    &__title,
    &__text {
      display: block;
    }

    &__title {
      font-size: 16px;
      line-height: 22px;
      font-weight: 600;
      color: #111827;
    }

    &__text {
      margin-top: 6px;
      font-size: 14px;
      line-height: 20px;
      color: #6b7280;
    }

    &__arrow {
      position: absolute;
      right: 18px;
      bottom: 18px;
      color: #0088cc;
    }
  }

  .main-insights {
    display: grid;
    grid-template-columns: minmax(0, 1.4fr) minmax(280px, .6fr);
    gap: 20px;
    margin-top: 20px;
  }

  .focus-card {
    display: grid;
    grid-template-columns: 48px minmax(0, 1fr) auto;
    align-items: center;
    gap: 18px;
    padding: 22px;

    &__icon {
      width: 48px;
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 8px;
      color: #04b756;
      background: rgba(4, 183, 86, 0.1);
    }

    &__title {
      font-size: 18px;
      line-height: 24px;
      font-weight: 600;
      color: #111827;
    }

    &__text {
      margin-top: 4px;
      font-size: 14px;
      line-height: 20px;
      color: #6b7280;
    }

    &__button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      min-height: 40px;
      padding: 0 16px;
      border-radius: 8px;
      background: #0088cc;
      color: #ffffff !important;
      font-weight: 600;
      font-size: 14px;
      line-height: 20px;
      transition: background .2s, transform .2s;

      &:hover {
        transform: translateY(-1px);
        background: #087fbb;
      }
    }
  }

  .help-card {
    padding: 22px;

    &__title {
      font-size: 18px;
      line-height: 24px;
      font-weight: 600;
      color: #111827;
    }

    &__text {
      margin-top: 8px;
      font-size: 14px;
      line-height: 20px;
      color: #6b7280;
    }

    &__link {
      display: inline-flex;
      margin-top: 18px;
      font-size: 14px;
      line-height: 20px;
      font-weight: 600;
      color: #0088cc !important;
    }
  }
}

@media (max-width: 1199px) {
  .main-page {
    .main-hero,
    .main-grid,
    .main-insights {
      grid-template-columns: 1fr;
    }

    .main-hero {
      flex-direction: column;

      &__actions {
        width: 100%;
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }
    }

    .main-grid {
      display: grid;
    }
  }
}

@media (max-width: 991px) {
  .main-page {
    .main-stats,
    .main-hero__actions,
    .workspace-actions {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .start-step {
      grid-template-columns: 40px minmax(0, 1fr);

      &__action {
        grid-column: 2;
        justify-content: flex-start;
      }
    }

    .focus-card {
      grid-template-columns: 48px minmax(0, 1fr);

      &__button {
        grid-column: 2;
        width: max-content;
      }
    }
  }
}

@media (max-width: 575px) {
  .main-page {
    .main-hero {
      padding: 22px;

      h1 {
        font-size: 26px;
        line-height: 34px;
      }
    }

    .main-stats,
    .main-hero__actions,
    .workspace-actions,
    .main-insights {
      grid-template-columns: 1fr;
    }

    .section-head {
      flex-direction: column;
    }

    .start-step,
    .focus-card {
      grid-template-columns: 1fr;

      &__action,
      &__button {
        grid-column: auto;
      }
    }
  }
}
</style>
